<template>
  <div class="highlight-reel-tab">
    <div v-if="highlights.length > 0" class="highlight-reel-actions">
      <a href="#" class="deselect-all-link" @click.prevent="requestDeselectAll">Deselect All</a>
      <VivaTooltip text="This creates a PDF with embedded links that can be shared">
        <button class="viva-btn viva-btn-orange viva-btn-small" @click="exportPdf" :disabled="pdfExporting">
          {{ pdfExporting ? 'Generating…' : 'Export as PDF' }}
        </button>
      </VivaTooltip>
    </div>
    <p v-if="pdfExportError" class="pdf-export-error">{{ pdfExportError }}</p>

    <div v-if="loading" class="empty-state">
      <i class="glyphicon glyphicon-refresh spinning"></i>
      <h3>Loading Highlight Reel...</h3>
    </div>

    <div v-else-if="error" class="empty-state">
      <i class="glyphicon glyphicon-exclamation-sign"></i>
      <p>{{ error }}</p>
    </div>

    <div v-else-if="highlights.length === 0" class="empty-state">
      <p>No highlighted questions yet. Check a question's box on its Review page to add it here.</p>
    </div>

    <div v-else class="highlight-grid">
      <div class="highlight-card" v-for="h in highlights" :key="h.question_id">
        <div class="highlight-card-header">
          <p class="highlight-assignment">{{ h.assignment_title }}</p>
          <p class="highlight-date">{{ formatDate(h.date_completed) }}</p>
        </div>

        <div class="highlight-video">
          <IVPlayer
            :ref="'player_' + h.question_id"
            preload="metadata"
            :videoSrc="getVideoUrl(h)"
            :questions="[]"
            :showMarkerTips="false"
            :customid="'highlight_' + h.question_id"
            @player-ready="onPlayerReady(h)"
          />
        </div>

        <p class="highlight-question">&ldquo;{{ h.question }}&rdquo;</p>

        <button class="viva-btn viva-btn-green viva-btn-small view-materials-btn" @click="viewMaterials(h)">
          VIEW MATERIALS
        </button>

        <label class="highlight-checkbox">
          <input type="checkbox" :checked="true" @change="requestUncheck(h, $event)" />
        </label>
      </div>
    </div>

    <div v-if="confirmTarget" class="modal-overlay" @click.self="cancelRemoval">
      <div class="confirm-modal">
        <p class="confirm-title">IMPORTANT:</p>
        <p class="confirm-body">
          Do you want to remove {{ confirmTarget === 'all' ? 'unchecked answers' : 'this answer' }}
          from the Highlight Reel?
        </p>
        <div class="confirm-actions">
          <button class="viva-btn viva-btn-green viva-btn-small" @click="confirmRemoval">YES</button>
          <a href="#" class="confirm-no-link" @click.prevent="cancelRemoval">NO</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import env from '../utils/env.js';
import IVPlayer from '@/components/IVPlayer.vue';
import VivaTooltip from '@/components/VivaTooltip.vue';

export default {
  name: 'HighlightReelTab',

  components: { IVPlayer, VivaTooltip },

  props: {
    studentId: { type: [Number, String], required: true },
    courseId: { type: [Number, String], required: true },
  },

  data() {
    return {
      highlights: [],
      loading: true,
      error: null,
      // null | 'all' | the single highlight object being unchecked
      confirmTarget: null,
      downloadingIds: new Set(),
      pdfExporting: false,
      pdfExportError: '',
    };
  },

  watch: {
    courseId: 'loadHighlights',
  },

  mounted() {
    this.loadHighlights();
  },

  methods: {
    async loadHighlights() {
      this.loading = true;
      this.error = null;
      try {
        const response = await apiClient.get(
          `/api/viva/instructor/student/${this.studentId}/highlight-reel/`,
          { params: { course_id: this.courseId } }
        );
        this.highlights = response.data.highlights;
      } catch (e) {
        console.error('Error loading Highlight Reel', e);
        this.error = 'Failed to load Highlight Reel.';
      } finally {
        this.loading = false;
      }
    },

    getVideoUrl(highlight) {
      if (!highlight.take_uid) return null;
      return `${env.staticAddr}/player/video/v1/file/${highlight.take_uid}/inline`;
    },

    onPlayerReady(highlight) {
      const ref = this.$refs['player_' + highlight.question_id];
      const player = Array.isArray(ref) ? ref[0] : ref;
      if (!player) return;
      // Load paused at the question's start time; stop again at the start of the
      // next question (end_timestamp) so the clip doesn't run into other answers.
      if (highlight.timestamp) player.seekToTimestamp(highlight.timestamp, { autoplay: false });
      player.setClipEnd(highlight.end_timestamp);
    },

    viewMaterials(highlight) {
      // Guards against a double-click firing two simultaneous downloads, matching
      // Review.vue's downloadStudentProject (downloadingProject flag).
      if (this.downloadingIds.has(highlight.question_id)) return;
      if (!highlight.paper_uid) {
        alert('Student project not available for download');
        return;
      }
      this.downloadingIds.add(highlight.question_id);
      const link = document.createElement('a');
      link.href = `${env.serverAddr}/video/v1/file/${highlight.paper_uid}/`;
      link.download = highlight.paper_title || `materials-${highlight.question_id}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => this.downloadingIds.delete(highlight.question_id), 1500);
    },

    requestUncheck(highlight, event) {
      // Revert the native checkbox immediately: it's bound to the literal `true`
      // (every card here is highlighted by definition), which never changes
      // between renders, so Vue has nothing to diff and won't re-patch the DOM
      // checked state back after the user's native click. The confirm modal
      // alone decides whether this card actually leaves the list.
      event.target.checked = true;
      this.confirmTarget = highlight;
    },

    requestDeselectAll() {
      if (this.highlights.length === 0) return;
      this.confirmTarget = 'all';
    },

    cancelRemoval() {
      this.confirmTarget = null;
    },

    async confirmRemoval() {
      const targets = this.confirmTarget === 'all' ? [...this.highlights] : [this.confirmTarget];
      this.confirmTarget = null;

      // allSettled, not all: these POSTs are independent, so one failing shouldn't
      // leave every other already-succeeded removal unreflected in the UI.
      const results = await Promise.allSettled(targets.map(h => apiClient.post(
        `/api/viva/instructor/question/${h.question_id}/highlight/`,
        { highlighted: false }
      )));

      const succeededIds = new Set();
      let failedCount = 0;
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          succeededIds.add(targets[i].question_id);
        } else {
          failedCount += 1;
          console.error('Error removing from Highlight Reel', targets[i].question_id, result.reason);
        }
      });

      this.highlights = this.highlights.filter(h => !succeededIds.has(h.question_id));

      if (failedCount > 0) {
        alert(failedCount === 1
          ? "Couldn't remove one answer from the Highlight Reel. Please try again."
          : `Couldn't remove ${failedCount} answers from the Highlight Reel. Please try again.`);
      }
    },

    // Printable PDF export is generated server-side (see shield/viva/instructor_views.py
    // student_highlight_reel_pdf) — same blob-download pattern as StudentSubmissions.vue's
    // exportPdf, since apiClient carries auth via a static Authorization header rather than
    // cookies, so the file can't just be linked/navigated to directly.
    async exportPdf() {
      this.pdfExporting = true;
      this.pdfExportError = '';
      try {
        const response = await apiClient.get(
          `/api/viva/instructor/student/${this.studentId}/highlight-reel/pdf/`,
          {
            params: { course_id: this.courseId, tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
            responseType: 'blob',
          }
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this._filenameFromContentDisposition(response.headers['content-disposition']) || 'HighlightReel.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error('PDF export failed:', e);
        this.pdfExportError = await this._extractPdfErrorMessage(e);
      } finally {
        this.pdfExporting = false;
      }
    },

    _filenameFromContentDisposition(header) {
      if (!header) return null;
      const match = header.match(/filename="?([^"]+)"?/);
      return match ? match[1] : null;
    },

    // responseType: 'blob' means even a JSON error body (e.g. a 404 for an unknown
    // student/course) arrives as a Blob, not parsed JSON — unwrap it best-effort,
    // falling back to the generic message on any failure to parse.
    async _extractPdfErrorMessage(error) {
      const fallback = 'Could not generate PDF. Please try again.';
      try {
        if (error.response && error.response.data instanceof Blob) {
          const parsed = JSON.parse(await error.response.data.text());
          if (parsed && parsed.error) return parsed.error;
        }
      } catch (_) {
        // not JSON, or no response body — fall through to the generic message
      }
      return fallback;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.highlight-reel-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 16px;
}

.deselect-all-link {
  color: var(--viva-green);
  font-size: 14px;
  font-weight: 600;
}

.pdf-export-error {
  color: #dc3545;
  font-size: 13px;
  margin: 0 0 16px 0;
}

.highlight-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
}

.highlight-card {
  background: white;
  border: 1.5px solid #E8E4DC;
  border-radius: 10px;
  padding: 16px;
  position: relative;
}

.highlight-card-header {
  text-align: center;
  margin-bottom: 10px;
}

.highlight-assignment {
  font-size: 14px;
  font-weight: 600;
  color: var(--viva-orange);
  margin: 0;
}

.highlight-date {
  font-size: 13px;
  color: var(--viva-grey);
  margin: 2px 0 0 0;
}

.highlight-video {
  margin-bottom: 10px;
}

.highlight-question {
  font-style: italic;
  font-size: 14px;
  color: var(--viva-dark);
  text-align: center;
  margin: 0 0 12px 0;
}

.view-materials-btn {
  display: block;
  margin: 0 auto;
}

.highlight-checkbox {
  position: absolute;
  top: 16px;
  left: 16px;
}

.highlight-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--viva-green);
}

.empty-state {
  text-align: center;
  color: var(--viva-grey);
  padding: 40px 20px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-modal {
  background: white;
  border: 2px solid var(--viva-orange);
  border-radius: 10px;
  padding: 28px 32px;
  max-width: 360px;
  text-align: center;
}

.confirm-title {
  color: var(--viva-orange);
  font-weight: 700;
  margin: 0 0 8px 0;
}

.confirm-body {
  color: var(--viva-dark);
  margin: 0 0 20px 0;
}

.confirm-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.confirm-no-link {
  color: var(--viva-green);
  font-weight: 500;
}
</style>
